<template>
  <div class="auth-page flex min-h-screen w-full items-center justify-center px-4">
    <div class="w-full max-w-[450px] text-center">
      <img class="mx-auto mb-6 block" height="45" src="@/assets/logo_black.png" alt="Periscope" />

      <BankIdAuthCard
        :message="message"
        :error="error"
        :qr-token="qrData"
        open-button-text="Öppna BankID på den här enheten"
        back-button-text="Tillbaka"
        @open-bankid="onOpenMobileBankID"
        @back="router.back()"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { BankIdAuthCard } from '@staff/ui'

import { applyAuthenticatedJWT, pollBankID, type UserAuthenticatedResponse } from '@/services/auth'
import { usePersonnelDirectory } from '@/composables/usePersonnelDirectory'
import { useMetadata } from '@/composables/useMetadata'

const route = useRoute()
const router = useRouter()
const { loadPersonnelDirectory } = usePersonnelDirectory()
const { loadMetadata } = useMetadata()

const statusCheckTimeout = ref<number>()
const autoStartToken = ref<string>('')
const qrData = ref<string>('')
const orderRef = ref<string>('')
const redirectUrl = ref<string | null>(null)
const authDevice = ref<string>('mobile')
const next = ref<string>('/ansokningar')
const message = ref<string>('')
const error = ref<string>('')
let isPollingActive = true

const BANK_ID_MESSAGES: Record<string, string> = {
  noClient: 'Starta BankID-appen',
  userMrtd: 'Fotografera och läs av din ID-handling med BankID-appen.',
  userCallConfirm: 'telefonmeddelande accepterat',
  started: 'Söker efter BankID, det kan ta en liten stund...',
  userSign: 'Fyll i din kod i BankID-appen',
  outstandingTransaction: 'Starta BankID i din mobil eller surfplatta',
  'outstandingTransaction-mobile': 'Starta BankID i din mobil eller surfplatta',
  'outstandingTransaction-desktop': 'Starta ditt BankID-program',
  startFailed:
    'BankID-appen verkar inte finnas i din dator eller telefon. Installera den och hämta ett BankID hos din internetbank. Installera appen från install.bankid.com',
  userCancel: 'Avbrutet',
  cancelled: 'Åtgärden avbruten. Försök igen',
  expiredTransaction: 'BankID-appen svarar inte. Starta din BankID-app och kontrollera att du har internetanslutning.',
  certificateErr:
    'Det BankID du försöker använda är för gammalt eller spärrat. Använd ett annat BankID eller hämta ett nytt hos din internetbank',
  userDeclinedCall: 'telefonmeddelande avböjt',
}

const mapHintCode = (hintCode?: string) => {
  if (!hintCode) {
    return 'Ett fel uppstod. Försök igen.'
  }

  if (hintCode === 'outstandingTransaction') {
    return BANK_ID_MESSAGES[`${hintCode}-${authDevice.value}`] ?? BANK_ID_MESSAGES[hintCode]
  }

  return BANK_ID_MESSAGES[hintCode] ?? hintCode
}

const handleSuccess = async (data: UserAuthenticatedResponse) => {
  isPollingActive = false
  if (statusCheckTimeout.value) {
    window.clearTimeout(statusCheckTimeout.value)
  }
  applyAuthenticatedJWT(data.jwt)
  await loadMetadata()
  try {
    await loadPersonnelDirectory(true)
  } catch {
    // Draft list retries this lookup; login should not be blocked by directory sync.
  }
  await router.push({ path: next.value })
}

const checkBankIDStatus = async () => {
  if (!isPollingActive || !orderRef.value) {
    throw new Error('Missing orderRef')
  }

  const data = await pollBankID(orderRef.value)

  if ('qrData' in data && data.qrData) {
    qrData.value = data.qrData
  }

  if ('user' in data) {
    await handleSuccess(data)
    return
  }

  if (data.status === 'pending') {
    message.value = mapHintCode(data.hintCode)
    statusCheckTimeout.value = window.setTimeout(() => {
      if (!isPollingActive) {
        return
      }

      checkBankIDStatus().catch((err) => {
        error.value = err instanceof Error ? err.message : 'Ett fel uppstod. Försök igen.'
      })
    }, 1000)
    return
  }

  error.value = mapHintCode(data.hintCode)
}

onMounted(async () => {
  orderRef.value = route.params.orderRef.toString()
  autoStartToken.value = route.query.at?.toString() ?? ''
  authDevice.value = route.query.de?.toString() ?? 'mobile'
  qrData.value = route.query.qr?.toString() ?? ''
  next.value = route.query.re?.toString() ?? '/ansokningar'
  redirectUrl.value = window.location.href

  try {
    await checkBankIDStatus()
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Ett fel uppstod. Försök igen.'
  }
})

onUnmounted(() => {
  isPollingActive = false
  if (statusCheckTimeout.value) {
    window.clearTimeout(statusCheckTimeout.value)
  }
})

const onOpenMobileBankID = () => {
  const url =
    /iphone/i.test(navigator.userAgent) && redirectUrl.value
      ? `https://app.bankid.com/?autostarttoken=${autoStartToken.value}&redirect=${encodeURIComponent(redirectUrl.value)}`
      : `bankid:///?autostarttoken=${autoStartToken.value}&redirect=null`

  if (statusCheckTimeout.value) {
    clearTimeout(statusCheckTimeout.value)
  }

  window.open(url)
}
</script>

<style scoped lang="scss">
.auth-page {
  background-color: var(--color-canvas-inset);
}
</style>
